import moment from "moment";

export const QUARTERS = {
  1: [7, 8, 9], // Jul-Sep
  2: [10, 11, 12], // Oct-Dec
  3: [1, 2, 3], // Jan-Mar
  4: [4, 5, 6], // Apr-Jun
};

export function monthsOfQuarter(fyStartYear, q) {
  // Build the 3 month moments for the given FY and quarter
  const months = QUARTERS[q] || [];
  return months.map((m) => {
    // Month indexing in moment: 0..11, here m is 1..12 natural month
    // If m >= 7 => calendar year = fyStartYear, else fyStartYear+1
    const year = m >= 7 ? fyStartYear : fyStartYear + 1;
    const firstDay = moment({ year, month: m - 1, day: 1 });
    return {
      y: year,
      m,
      label: firstDay.format("MMM YYYY"),
      isoMonth: firstDay.format("YYYY-MM"),
      start: firstDay.startOf("month"),
      end: firstDay.endOf("month"),
    };
  });
}

/**
 * Derive per-month reporting statuses for "monthly" mode.
 * submissions: array with .createdOn (ISO) and optional .periodMonth ('YYYY-MM')
 */
export function computeMonthlyStatuses({
  months,
  submissions = [],
  now = moment(),
}) {
  const submittedSet = new Set(
    submissions.filter((s) => !!s.month).map((s) => s.month)
  );

  return months.map((m) => {
    const isSubmitted = submittedSet.has(m);
    const isPast = now.isAfter(m.end, "day");
    return {
      ...m,
      status: isSubmitted ? "reported" : isPast ? "missed" : "pending",
    };
  });
}

/**
 * RBM_CalculateScore Mirror.
 * qualityValue is supplied by supervisor (e.g. 95, 85, 75, 60, 40)
 */
export function rbmCalculateScore({
  measurementCode,
  target,
  achievedTarget,
  qualityValue = 95,
  isCompleted,
}) {
  let score;

  if (+target === 0) {
    if (measurementCode === "YN") {
      score = isCompleted ? 100 : achievedTarget || 0;
    } else {
      score = achievedTarget > 0 ? achievedTarget : 0;
    }
  } else {
    switch (measurementCode) {
      case "%UP":
      case "SUM":
      case "MS":
      case "POINTS":
        score = (achievedTarget / target) * 100;
        break;
      case "%DOWN":
        score = achievedTarget > 0 ? (target / achievedTarget) * 100 : 0;
        break;
      case "YN":
        score = isCompleted ? 100 : (achievedTarget / 100.0) * 100;
        break;
      case "REPEAT":
        score =
          achievedTarget >= target ? 100 : (achievedTarget / target) * 100;
        break;
      case "FIXED":
        score = achievedTarget === target ? 100 : 0;
        break;
      case "RANGE":
        score = achievedTarget === target ? 100 : 0;
        break;
      case "RANK":
        score = achievedTarget <= target ? 100 : 80;
        break;
      default:
        score = (achievedTarget / target) * 100;
    }
  }

  score = Math.max(0, Math.min(100, score));
  return Math.round(score * qualityValue) / 100;
}

/**
 * RBM_CalculateTimePenalty Mirror.
 * inputs:
 *  - itemTypeId (1,2,3)
 *  - deadline (ISO or moment)
 *  - submissions: [{ createdOn: ISO }]
 *  - rejections:  [{ createdOn: ISO }]
 * Notes:
 *  First late day = 5; subsequent late days = +1.
 *  After each rejection: anchor = rejection time + (1 day if in working hours [08:00-17:00], else +2 days)
 *  Then compare next submission time to the anchor for extra late days.
 */
export function rbmCalculateTimePenalty({
  deadline,
  submissions = [],
  rejections = [],
}) {
  if (!deadline || !submissions.length) return 0;

  const WorkStart = moment("08:00", "HH:mm");
  const WorkEnd = moment("17:00", "HH:mm");
  const dl = moment(deadline);

  const subs = submissions
    .map((s) => moment(s.createdOn))
    .filter((d) => d.isValid())
    .sort((a, b) => a.valueOf() - b.valueOf());

  const rejs = rejections
    .map((r) => moment(r.createdOn))
    .filter((d) => d.isValid())
    .sort((a, b) => a.valueOf() - b.valueOf());

  if (!subs.length) return 0;

  let penalty = 0;
  let hasFirstPremium = false;

  // 1) initial lateness vs original deadline
  const firstSub = subs[0];
  const initialLateDays = Math.max(
    0,
    firstSub.startOf("day").diff(dl.startOf("day"), "days")
  );
  if (initialLateDays > 0) {
    penalty += 5 + (initialLateDays - 1);
    hasFirstPremium = true;
  }

  // 2) cycles after rejections
  let sumDays = 0;
  let firstLateDaysInCycles = null;

  rejs.forEach((rej) => {
    // anchor
    const t = moment(rej);
    const inWorkingHours = moment(t.format("HH:mm"), "HH:mm").isBetween(
      WorkStart,
      WorkEnd,
      null,
      "[]"
    );
    const anchor = moment(t)
      .add(inWorkingHours ? 1 : 2, "days")
      .startOf("day");

    // next submission after rej
    const nextSub = subs.find((s) => s.isAfter(rej));
    if (!nextSub) return;

    const daysLate = Math.max(0, nextSub.startOf("day").diff(anchor, "days"));
    if (daysLate > 0) {
      sumDays += daysLate;
      if (firstLateDaysInCycles == null) firstLateDaysInCycles = daysLate;
    }
  });

  if (sumDays > 0) {
    if (hasFirstPremium) {
      penalty += sumDays;
    } else {
      // first late day premium applies once
      penalty +=
        5 + (firstLateDaysInCycles - 1) + (sumDays - firstLateDaysInCycles);
    }
  }
  return penalty;
}

/**
 * Quality penalty: 5 marks per rejection (uncanceled).
 */
export function qualityPenaltyFromRejections({ rejectionsCount = 0 }) {
  return 5 * (rejectionsCount || 0);
}

/**
 * Expected Max Score for preview:
 *  - Computes core score via rbmCalculateScore
 *  - Multiplies by (activityWeight / totalWeight)
 *  - Applies penalties: time penalty + quality penalty
 *  - Clamps to [0,100]
 */
export function computeExpectedMaxScore({
  measurementCode,
  target,
  achievedTarget,
  qualityValue,
  isCompleted,
  activityWeight,
  totalWeight,
  timePenalty=0,
  qualityPenalty=0,
}) {
  const base = rbmCalculateScore({
    measurementCode,
    target: Number(target || 0),
    achievedTarget: Number(achievedTarget || 0),
    qualityValue: Number(qualityValue || 95)/100,
    isCompleted: !!isCompleted,
  });

  const weighted =
    (Number(activityWeight || 0) / Number(totalWeight || 1)) * base;

  const penalized = Math.max(
    0,
    weighted - Number(timePenalty || 0) - Number(qualityPenalty || 0)
  );
  return Math.max(0, Math.min(100, Math.round(penalized * 100) / 100));
}

/**
 * Returns input constraints + UX hints for a measurement code.
 * forceEquals: when only an exact value is valid (e.g. FIXED).
 * isLowerBetter: use to flip warnings/hints (0 may be “best”).
 */
export const getMeasurementBounds = ({ code = "SUM", target = 0 }) => {
  const t = Number(target) || 0;
  switch ((code || "SUM").toUpperCase()) {
    case "SUM":
      return {
        min: 0,
        max: t,
        isLowerBetter: false,
        hint: `Allowed range: 0 to ${t}.`,
      };
    case "UP%":
    case "UPPERCENT":
    case "UP-PERCENT":
      return {
        min: 0,
        max: t,
        isLowerBetter: false,
        hint: `Allowed range: 0% to ${t}%. Higher is better.`,
      };
    case "LOWER%":
    case "LOWERPERCENT":
    case "LOWER-PERCENT":
      // lower is better (best is 0), but still enforce 0..100
      return {
        min: 0,
        max: 100,
        isLowerBetter: true,
        hint: `Allowed range: 0% to 100%. Lower is better (0 is best).`,
      };
    case "EXACT":
    case "FIXED":
      return {
        min: t,
        max: t,
        forceEquals: true,
        isLowerBetter: false,
        hint: `Value must equal ${t}.`,
      };
    default:
      // safe fallback
      return {
        min: 0,
        max: t || 100,
        isLowerBetter: false,
        hint: t ? `Allowed range: 0 to ${t}.` : `Enter a non-negative value.`,
      };
  }
};